/**
 * Fallback for SFUT Legacy gallery cards whose explorer instance has no
 * metadata yet (explorer indexer has not fetched tokenURI). Reads tokenURI on
 * chain (read-only eth_call) and loads the JSON through our IPFS gateways.
 */
import { APERTUM_RPC } from './apertumChain';
import { DEFAULT_IPFS_GATEWAYS, IPFS_FETCH_TIMEOUT_MS, gatewayUrl } from './ipfs';

export type ExplorerInstance = {
  id: string;
  image_url?: string | null;
  metadata?: Record<string, unknown> | null;
  [key: string]: unknown;
};

type FetchLike = typeof fetch;

/** tokenURI(uint256) selector. */
const TOKEN_URI_SELECTOR = '0xc87b56dd';

export function encodeTokenUriCall(tokenId: string | number | bigint): string {
  const id = BigInt(tokenId);
  if (id < BigInt(0)) throw new Error('Negative token id');
  return `${TOKEN_URI_SELECTOR}${id.toString(16).padStart(64, '0')}`;
}

/** Decode an ABI-encoded single `string` return value. */
export function decodeAbiString(hex: string): string {
  const body = hex.startsWith('0x') ? hex.slice(2) : hex;
  if (body.length < 128) throw new Error('Short ABI string');
  const offset = parseInt(body.slice(0, 64), 16) * 2;
  const length = parseInt(body.slice(offset, offset + 64), 16);
  const dataHex = body.slice(offset + 64, offset + 64 + length * 2);
  const bytes = new Uint8Array(dataHex.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = parseInt(dataHex.slice(i * 2, i * 2 + 2), 16);
  }
  return new TextDecoder().decode(bytes);
}

export async function readTokenUri(
  collection: string,
  tokenId: string,
  opts?: { rpcUrl?: string; fetchImpl?: FetchLike }
): Promise<string> {
  const fetchImpl = opts?.fetchImpl ?? fetch;
  const res = await fetchImpl(opts?.rpcUrl ?? APERTUM_RPC, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method: 'eth_call',
      params: [{ to: collection, data: encodeTokenUriCall(tokenId) }, 'latest'],
    }),
  });
  if (!res.ok) throw new Error(`RPC HTTP ${res.status}`);
  const json = (await res.json()) as { result?: string; error?: { message?: string } };
  if (!json.result || json.result === '0x') {
    throw new Error(json.error?.message || 'Empty tokenURI result');
  }
  return decodeAbiString(json.result);
}

/**
 * tokenURI for many ids in ONE JSON-RPC batch request (one round trip instead of one per
 * token). Falls back to parallel single calls if the RPC does not answer the batch.
 */
export async function readTokenUris(
  collection: string,
  ids: string[],
  opts?: { rpcUrl?: string; fetchImpl?: FetchLike }
): Promise<Record<string, string>> {
  if (!ids.length) return {};
  const fetchImpl = opts?.fetchImpl ?? fetch;
  const out: Record<string, string> = {};
  try {
    const res = await fetchImpl(opts?.rpcUrl ?? APERTUM_RPC, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(
        ids.map((id, i) => ({
          jsonrpc: '2.0',
          id: i,
          method: 'eth_call',
          params: [{ to: collection, data: encodeTokenUriCall(id) }, 'latest'],
        }))
      ),
    });
    const json = (await res.json()) as unknown;
    if (!res.ok || !Array.isArray(json)) throw new Error('RPC batch not supported');
    for (const row of json as Array<{ id?: number; result?: string }>) {
      const id = typeof row.id === 'number' ? ids[row.id] : undefined;
      if (id !== undefined && row.result && row.result !== '0x') {
        try {
          out[id] = decodeAbiString(row.result);
        } catch {
          /* skip */
        }
      }
    }
    return out;
  } catch {
    const settled = await Promise.allSettled(ids.map((id) => readTokenUri(collection, id, opts)));
    settled.forEach((r, i) => {
      if (r.status === 'fulfilled') out[ids[i]] = r.value;
    });
    return out;
  }
}

/** Promise.any (ES2021) for the ES2020 lib: first fulfilled value, or reject when all reject. */
export function firstFulfilled<T>(promises: Array<Promise<T>>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let left = promises.length;
    if (!left) reject(new Error('No promises'));
    for (const p of promises) {
      p.then(resolve, () => {
        left -= 1;
        if (left === 0) reject(new Error('All promises rejected'));
      });
    }
  });
}

/**
 * GET IPFS JSON from every gateway at once and take the first good answer
 * (firstFulfilled); the losers are aborted. Measured: Filebase ~0.1-0.2 s,
 * Pinata public ~5-7 s, dweb.link often 429, so racing never waits on a slow one.
 */
export async function raceIpfsJson<T = unknown>(
  cidOrUri: string,
  opts?: { gateways?: readonly string[]; fetchImpl?: FetchLike; timeoutMs?: number }
): Promise<T> {
  const gateways = opts?.gateways ?? DEFAULT_IPFS_GATEWAYS;
  const fetchImpl = opts?.fetchImpl ?? fetch;
  const controllers = gateways.map(() => new AbortController());
  const timer = setTimeout(
    () => controllers.forEach((c) => c.abort()),
    opts?.timeoutMs ?? IPFS_FETCH_TIMEOUT_MS
  );
  try {
    return await firstFulfilled(
      gateways.map(async (gw, i) => {
        const res = await fetchImpl(gatewayUrl(cidOrUri, gw), { signal: controllers[i].signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as T;
        controllers.forEach((c, j) => {
          if (j !== i) c.abort();
        });
        return data;
      })
    );
  } catch {
    throw new Error(`IPFS fetch failed on all gateways for ${cidOrUri.slice(0, 80)}`);
  } finally {
    clearTimeout(timer);
  }
}

export function needsMetadataFallback(item: ExplorerInstance): boolean {
  const meta = item.metadata;
  return !meta || (!meta.image && !meta.name);
}

type HydrateDeps = {
  readUri?: (collection: string, tokenId: string) => Promise<string>;
  readUris?: (collection: string, tokenIds: string[]) => Promise<Record<string, string>>;
  fetchJson?: (uri: string) => Promise<Record<string, unknown>>;
};

/**
 * Return items with missing explorer metadata filled from tokenURI.
 * Items that already have metadata, or whose fallback fails, are unchanged.
 * All tokenURIs are read in one RPC batch, then every metadata JSON is fetched in
 * parallel with the gateways raced.
 */
export async function hydrateMissingMetadata(
  collection: string,
  items: ExplorerInstance[],
  deps: HydrateDeps = {}
): Promise<ExplorerInstance[]> {
  const fetchJson =
    deps.fetchJson ?? ((uri: string) => raceIpfsJson<Record<string, unknown>>(uri));
  const missing = items.filter(needsMetadataFallback).map((item) => String(item.id));
  if (!missing.length) return items;

  let uris: Record<string, string> = {};
  if (deps.readUri) {
    const read = deps.readUri;
    const settled = await Promise.allSettled(missing.map((id) => read(collection, id)));
    settled.forEach((r, i) => {
      if (r.status === 'fulfilled') uris[missing[i]] = r.value;
    });
  } else {
    uris = await (deps.readUris ?? ((c: string, ids: string[]) => readTokenUris(c, ids)))(
      collection,
      missing
    ).catch(() => ({}));
  }

  return Promise.all(
    items.map(async (item) => {
      if (!needsMetadataFallback(item)) return item;
      const uri = uris[String(item.id)];
      if (!uri) return item;
      try {
        const metadata = await fetchJson(uri);
        if (!metadata || typeof metadata !== 'object') return item;
        const image = typeof metadata.image === 'string' ? metadata.image : null;
        return { ...item, metadata, image_url: item.image_url || image };
      } catch {
        return item;
      }
    })
  );
}
